import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema, BooleanQueryParamSchema } from './common.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';
import { MasterCodeSchema } from './query-types.js';

/**
 * Trip-type master (Masters → Trip Types). Operator-curated catalogue
 * of FIT / Group / Cruise / etc. Associated with destinations via the
 * `destination_trip_types` join so a destination can advertise which
 * trip types it supports, and the user-register form filters its
 * destination picker by the operator-selected trip type. A pure
 * simple master - mirrors Department in shape (ADR-0016 / ADR-0020).
 */

export const TripTypeListItemSchema = z.object({
  id: UuidSchema,
  legacyId: z.number().int().nullable(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  isActive: z.boolean(),
  sortOrder: z.number().int(),
  createdAt: IsoDateTimeSchema,
});
export type TripTypeListItem = z.infer<typeof TripTypeListItemSchema>;

export const TripTypeListQuerySchema = PaginationQuerySchema.extend({
  isActive: BooleanQueryParamSchema.optional(),
  includeDeleted: BooleanQueryParamSchema.optional(),
});
export type TripTypeListQuery = z.infer<typeof TripTypeListQuerySchema>;

export const TripTypeListResponseSchema = paginatedResponseSchema(TripTypeListItemSchema);
export type TripTypeListResponse = z.infer<typeof TripTypeListResponseSchema>;

export const TripTypeDetailSchema = TripTypeListItemSchema.extend({
  updatedAt: IsoDateTimeSchema,
  deletedAt: IsoDateTimeSchema.nullable(),
});
export type TripTypeDetail = z.infer<typeof TripTypeDetailSchema>;

const TripTypeCoreFieldsSchema = z.object({
  code: MasterCodeSchema,
  name: z.string().trim().min(1, 'Name is required').max(120),
  description: z.string().trim().max(500).nullable().optional(),
  legacyId: z.number().int().positive().nullable().optional(),
  isActive: z.boolean().optional(),
  sortOrder: z.number().int().min(0).optional(),
});

export const CreateTripTypeRequestSchema = TripTypeCoreFieldsSchema;
export type CreateTripTypeRequest = z.infer<typeof CreateTripTypeRequestSchema>;

export const UpdateTripTypeRequestSchema = TripTypeCoreFieldsSchema.partial();
export type UpdateTripTypeRequest = z.infer<typeof UpdateTripTypeRequestSchema>;

// Shared "haul" enum (mirrors the Postgres `DestinationHaul` type).
// Used by the Destination master + surfaced by the user-form filter.
export const DestinationHaulSchema = z.enum(['SHORT_HAUL', 'LONG_HAUL']);
export type DestinationHaul = z.infer<typeof DestinationHaulSchema>;
export const DESTINATION_HAULS = DestinationHaulSchema.options;

// Small ref shape used inline on Destination/TripType joins.
export const TripTypeRefSchema = z.object({
  id: UuidSchema,
  code: z.string(),
  name: z.string(),
});
export type TripTypeRef = z.infer<typeof TripTypeRefSchema>;
